/**
 * WorkItemCard component
 * Displays Azure DevOps work item details
 */
import { Bug, BookOpen, CheckSquare, Layers, User, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface WorkItem {
  /** Work item ID */
  id: number
  /** Work item type (Bug, User Story, Task, Feature) */
  type: 'Bug' | 'User Story' | 'Task' | 'Feature' | string
  /** Work item title */
  title: string
  /** Current state */
  state: string
  /** Assigned user name */
  assignedTo?: string
  /** URL to the work item in ADO */
  url: string
  /** Priority (1-4) */
  priority?: number
  /** Severity for bugs */
  severity?: string
  /** Story points for user stories */
  storyPoints?: number
}

export interface WorkItemCardProps {
  /** Work item data */
  workItem: WorkItem
  /** Display in compact mode */
  compact?: boolean
}

const typeConfig: Record<string, { icon: typeof Bug; color: string }> = {
  Bug: { icon: Bug, color: 'text-red-500' },
  'User Story': { icon: BookOpen, color: 'text-blue-500' },
  Task: { icon: CheckSquare, color: 'text-yellow-500' },
  Feature: { icon: Layers, color: 'text-purple-500' },
}

const stateColors: Record<string, string> = {
  New: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  Active: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  Resolved: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  Closed: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  Completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  'In Progress': 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
}

export function WorkItemCard({ workItem, compact = false }: WorkItemCardProps) {
  const config = typeConfig[workItem.type] || typeConfig.Task
  const Icon = config.icon

  return (
    <article
      role="article"
      aria-label={`${workItem.type} ${workItem.id}`}
      data-work-item-type={workItem.type}
      data-state={workItem.state}
      data-compact={compact}
      className={cn(
        'rounded-lg border bg-card text-card-foreground',
        compact ? 'p-2' : 'p-4'
      )}
    >
      {/* Header with type icon and ID */}
      <div className="flex items-start gap-3">
        <div className={cn('flex-shrink-0 mt-0.5', config.color)}>
          <Icon className={cn(compact ? 'h-4 w-4' : 'h-5 w-5')} />
        </div>
        <div className="flex-1 min-w-0">
          {/* Type and ID */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('font-medium', config.color, compact ? 'text-xs' : 'text-sm')}>
              {workItem.type}
            </span>
            <span className="text-muted-foreground text-sm">#{workItem.id}</span>
            <span
              className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                stateColors[workItem.state] || stateColors.New
              )}
            >
              {workItem.state}
            </span>
          </div>

          {/* Title */}
          <a
            href={workItem.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'block mt-1 font-medium hover:underline text-foreground',
              compact ? 'text-sm line-clamp-1' : 'text-base'
            )}
          >
            {workItem.title}
            <ExternalLink className="inline-block ml-1 h-3 w-3 text-muted-foreground" />
          </a>

          {/* Details row */}
          {!compact && (
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
              {/* Assignee */}
              <div className="flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                <span>{workItem.assignedTo || 'Unassigned'}</span>
              </div>

              {/* Priority */}
              {workItem.priority !== undefined && (
                <span className="px-1.5 py-0.5 rounded bg-muted text-xs">
                  P{workItem.priority}
                </span>
              )}

              {/* Severity (for bugs) */}
              {workItem.severity && (
                <span className="text-xs">{workItem.severity}</span>
              )}

              {/* Story points (for user stories) */}
              {workItem.storyPoints !== undefined && (
                <span className="px-1.5 py-0.5 rounded bg-muted text-xs">
                  {workItem.storyPoints} pts
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  )
}
